import { useCallback, useEffect, useRef } from 'react'
import { useAuth } from 'react-oidc-context'
import AcrossTabsModule, { MessageData } from 'across-tabs'

const IDLE_EVENTS: readonly string[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'wheel',
  'touchstart',
  'pointerdown',
]

export const useIdleLogout = (timeoutMinutes = 15): void => {
  const timeoutRef = useRef<number | null>(null)
  // Wall-clock deadline so effect re-runs (e.g. auth object identity changes) do not
  // restart a full 15 minutes — they only re-arm the remaining idle time.
  const idleDeadlineRef = useRef<number | null>(null)
  const isLoggingOutRef = useRef(false)
  const tabIdRef = useRef<string>(crypto.randomUUID())

  const acrossTabsRef = useRef<InstanceType<typeof AcrossTabsModule.Parent> | null>(null)
  const broadcastRef = useRef<BroadcastChannel | null>(null)

  const auth = useAuth()
  // Keep signoutRedirect current without putting the whole auth object in callback deps.
  // Otherwise USER_LOADED from silent renew recreates logout → resetTimer → clears the idle timeout.
  const signoutRedirectRef = useRef(auth.signoutRedirect)
  signoutRedirectRef.current = auth.signoutRedirect

  const timeoutMs = timeoutMinutes * 60 * 1000

  const clearTimer = useCallback((): void => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const logout = useCallback((): void => {
    if (isLoggingOutRef.current) return
    isLoggingOutRef.current = true
    idleDeadlineRef.current = null

    console.log('User inactive. Logging out…')

    broadcastRef.current?.postMessage({
      type: 'logout',
      source: tabIdRef.current,
    })

    void signoutRedirectRef.current().catch((err: unknown) => {
      console.error('Logout error:', err)
      isLoggingOutRef.current = false
    })
  }, [])

  const resetTimer = useCallback((): void => {
    idleDeadlineRef.current = Date.now() + timeoutMs
    clearTimer()
    timeoutRef.current = window.setTimeout(logout, timeoutMs)
  }, [clearTimer, logout, timeoutMs])

  const armTimer = useCallback((): void => {
    const now = Date.now()
    const deadline = idleDeadlineRef.current

    if (deadline === null || deadline <= now) {
      resetTimer()
      return
    }

    clearTimer()
    timeoutRef.current = window.setTimeout(logout, deadline - now)
  }, [clearTimer, logout, resetTimer])

  useEffect(() => {
    if (!auth.isAuthenticated) {
      clearTimer()
      idleDeadlineRef.current = null
      isLoggingOutRef.current = false
      return
    }

    armTimer()

    for (const event of IDLE_EVENTS) {
      window.addEventListener(event, resetTimer, { capture: true })
    }

    try {
      acrossTabsRef.current = new AcrossTabsModule.Parent({
        onChildCommunication: (message: MessageData) => {
          if (message.data === 'CHILD_ACTIVE') {
            console.log('Activity from child tab — resetting timer')
            resetTimer()
          }
        },
      })
    } catch (err) {
      console.error('AcrossTabsModule init failed:', err)
    }

    if ('BroadcastChannel' in window) {
      const bc = new BroadcastChannel('logout_channel')
      broadcastRef.current = bc

      bc.onmessage = (event: MessageEvent) => {
        if (event.data?.type === 'logout' && event.data.source !== tabIdRef.current) {
          logout()
        }
      }
    }

    return () => {
      // Clear the pending timeout only; keep idleDeadlineRef so a re-run can re-arm remaining time.
      clearTimer()

      for (const event of IDLE_EVENTS) {
        window.removeEventListener(event, resetTimer, { capture: true })
      }

      broadcastRef.current?.close()
      broadcastRef.current = null

      acrossTabsRef.current = null
    }
  }, [auth.isAuthenticated, armTimer, clearTimer, resetTimer, logout])
}

export default useIdleLogout
